import { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";

import {
  getUser,
  getAirportById,
  getNonProfitById,
} from "../services/graphqlService";

function extractAssociationsFromUser(data) {
  if (!data) return [];
  const raw =
    data.association ??
    data.associations ??
    data.profile?.association ??
    data.profile?.associations ??
    [];
  return Array.isArray(raw) ? raw : [];
}

function formatEntityTypeLabel(entityType) {
  if (!entityType) return "—";
  return String(entityType)
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

async function enrichAssociationsWithOrgNames(associations, idToken) {
  const list = Array.isArray(associations) ? associations : [];
  return Promise.all(
    list.map(async (a) => {
      if (a?.entityName) return { ...a };
      const id = a?.entityId;
      const type = String(a?.entityType || "").toLowerCase();
      if (!id) return { ...a };
      try {
        if (type === "nonprofit") {
          const res = await getNonProfitById(id, idToken);
          const name = res?.data?.name;
          return name ? { ...a, entityName: name } : { ...a };
        }
        if (type === "airport") {
          const res = await getAirportById(id, idToken);
          const name = res?.data?.name;
          return name ? { ...a, entityName: name } : { ...a };
        }
      } catch {
        // leave association unchanged
      }
      return { ...a };
    }),
  );
}

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  const display = Array.isArray(value)
    ? value
        .map((v) => (typeof v === "object" ? JSON.stringify(v) : v))
        .join(", ")
    : typeof value === "object"
    ? JSON.stringify(value, null, 2)
    : String(value);
  return (
    <Grid item xs={12} sm={6}>
      <SoftBox
        py={1}
        px={1.5}
        sx={{
          borderRadius: 1,
          bgcolor: "grey.50",
        }}
      >
        <SoftTypography
          variant="caption"
          color="text"
          fontWeight="bold"
          display="block"
        >
          {label}
        </SoftTypography>
        <SoftTypography variant="body2" fontWeight="regular" color="text">
          {display}
        </SoftTypography>
      </SoftBox>
    </Grid>
  );
}

function SectionTitle({ children }) {
  return (
    <SoftTypography
      variant="button"
      fontWeight="bold"
      color="text"
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        mb: 1,
        display: "block",
      }}
    >
      {children}
    </SoftTypography>
  );
}

function ViewUserModal({ open, onClose, profileId, idToken }) {
  const [user, setUser] = useState(null);
  const [enrichedAssociations, setEnrichedAssociations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !profileId || !idToken) {
      setUser(null);
      setEnrichedAssociations([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setUser(null);
    setEnrichedAssociations([]);

    getUser(profileId, idToken)
      .then(async (res) => {
        if (cancelled) return;
        const data = res.data;
        const rawList = extractAssociationsFromUser(data);
        const enriched = await enrichAssociationsWithOrgNames(rawList, idToken);
        if (cancelled) return;
        setUser(data);
        setEnrichedAssociations(enriched);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load user");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, profileId, idToken]);

  const handleClose = () => {
    setUser(null);
    setEnrichedAssociations([]);
    setError(null);
    onClose();
  };

  const u = user || {};
  const profile = u.profile || {};
  const loc = u.location || profile?.location || {};
  const associationList =
    enrichedAssociations.length > 0
      ? enrichedAssociations
      : extractAssociationsFromUser(u);

  const organizationNames = [
    ...new Set(
      associationList.map((a) => a.entityName).filter(Boolean),
    ),
  ];
  const organizationSummary =
    organizationNames.length > 0 ? organizationNames.join(", ") : null;

  const displayName = u.name ?? profile?.name ?? "—";
  const displayRole = u.role ?? profile?.role;
  const displayStatus = u.status ?? profile?.status;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.4)" },
      }}
    >
      <Card
        sx={{
          width: "95%",
          maxWidth: 720,
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: 24,
          borderRadius: 2,
        }}
      >
        <SoftBox p={3}>
          {/* Header */}
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <SoftBox>
              <SoftTypography variant="h4" fontWeight="bold" color="dark">
                {displayName}
              </SoftTypography>
              {!loading && organizationSummary && (
                <SoftTypography
                  variant="body2"
                  color="text"
                  sx={{ mt: 0.75, fontWeight: 500 }}
                >
                  {organizationNames.length > 1
                    ? "Organizations: "
                    : "Organization: "}
                  {organizationSummary}
                </SoftTypography>
              )}
            </SoftBox>
            <SoftButton
              variant="text"
              color="secondary"
              iconOnly
              onClick={handleClose}
            >
              <Icon>close</Icon>
            </SoftButton>
          </SoftBox>

          <Divider sx={{ mb: 2 }} />

          {loading && (
            <SoftBox display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </SoftBox>
          )}

          {error && (
            <SoftBox mt={1} mb={2}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </SoftBox>
          )}

          {!loading && !error && user && (
            <SoftBox>
              {/* Contact */}
              <SectionTitle>Contact</SectionTitle>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <DetailRow label="Email" value={u.email ?? profile?.email} />
                <DetailRow label="Phone" value={u.phone ?? profile?.phone} />
                <DetailRow label="Mobile" value={u.mobile ?? profile?.mobile} />
                <DetailRow
                  label="Employee ID"
                  value={u.employeeId ?? profile?.employeeId}
                />
                <DetailRow
                  label={
                    organizationNames.length > 1 ? "Organizations" : "Organization"
                  }
                  value={organizationSummary}
                />
              </Grid>

              {/* Location */}
              <SectionTitle>Location</SectionTitle>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                <DetailRow label="City" value={loc?.city ?? u.city} />
                <DetailRow label="State" value={loc?.state ?? u.state} />
                <DetailRow
                  label="Location ID"
                  value={loc?.locationId ?? u.locationId}
                />
              </Grid>

              {/* Associations - List (always show section) */}
              <SoftBox sx={{ mb: 3 }}>
                <SectionTitle>Associations</SectionTitle>
                <List
                  dense
                  disablePadding
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "grey.50",
                  }}
                >
                  {associationList.length > 0 ? (
                    associationList.map((a, i) => (
                      <ListItem
                        key={i}
                        divider={i < associationList.length - 1}
                        sx={{ py: 1.25, px: 2 }}
                      >
                        <ListItemText
                          primary={
                            <SoftBox
                              display="flex"
                              flexDirection="column"
                              alignItems="flex-start"
                              gap={0.5}
                            >
                              <SoftBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                              >
                                <SoftBadge
                                  variant="contained"
                                  badgeContent={a.role || "—"}
                                  color="info"
                                  size="xs"
                                  container
                                />
                                <SoftTypography
                                  variant="body2"
                                  fontWeight="bold"
                                  color="dark"
                                  component="span"
                                >
                                  {a.entityName ||
                                    formatEntityTypeLabel(a.entityType)}
                                  {!a.entityName && a.entityId
                                    ? ` (ID: ${a.entityId})`
                                    : ""}
                                </SoftTypography>
                              </SoftBox>
                              {a.entityName && (
                                <SoftTypography
                                  variant="caption"
                                  color="secondary"
                                  component="span"
                                >
                                  {formatEntityTypeLabel(a.entityType)}
                                  {a.entityId ? ` · ID: ${a.entityId}` : ""}
                                </SoftTypography>
                              )}
                            </SoftBox>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem sx={{ py: 2, px: 2 }}>
                      <ListItemText
                        primary={
                          <SoftTypography variant="body2" color="secondary">
                            No associations
                          </SoftTypography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </SoftBox>
            </SoftBox>
          )}

          {!loading && !error && !user && open && profileId && (
            <SoftBox py={5} textAlign="center">
              <SoftTypography variant="body2" color="text">
                No user data returned.
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
      </Card>
    </Modal>
  );
}

export default ViewUserModal;
